import { deleteDB, openDB } from 'idb';

import * as Navigation from "@ui-archipad/drivers/navigation";
import { closeAppDB, DB_NAME, suspendAppDB } from "@ui-archipad/helpers/indexedDB";

/** How long the page is blocked at most while clearing: what is not cleared by then is reported */
export const CLEAR_ALL_CACHES_TIMEOUT_MS = 60 * 1000;
/** How much longer the other pages wait for the end of a clear before reloading anyway: the clearing page may be closed */
const OTHER_PAGES_MARGIN_MS = 15 * 1000;
/**
 * The databases every page keeps open until it unloads, without closing them for a deletion (`versionchange`): the
 * queue of the Sentry events not sent yet (`Sentry.makeBrowserOfflineTransport`). Emptied instead of deleted
 */
const DATABASES_KEPT_OPEN = ['sentry-offline'];

/**
 * The pages of the application tell each other about a clear: a database is only deleted once every connection to it
 * is closed, and a page that keeps running during the clear recreates what is deleted (its database, its session).
 * - `start`: a page clears, the others close their database, are blocked, and wait for `end`
 * - `end`: the clear is done, the others reload
 * - `query`: a page that starts asks whether a clear is in progress, the clearing page answers `start`
 */
type ClearAllCachesMessage = { type: 'start'; timeoutMs: number } | { type: 'end' } | { type: 'query' };

export interface ClearAllCachesResult {
    /** What is still there when the time is up (a database still open in another window...): empty when all is cleared */
    notCleared: string[];
}

// a single channel for sending and receiving: a page does not receive its own messages
const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('archipad-clear-all-caches');
/** The timeout of the clear this page runs */
let clearingTimeoutMs: number | null = null;
/** Ends the wait of this page for the clear of another one */
let endOtherPageClear: (() => void) | null = null;

channel?.addEventListener('message', ({ data }: MessageEvent<ClearAllCachesMessage>) => {
    switch (data?.type) {
        case 'query':
            if (clearingTimeoutMs !== null) {
                post({ type: 'start', timeoutMs: clearingTimeoutMs });
            }
            break;
        case 'start':
            waitForOtherPageClear(data.timeoutMs).catch((error) => console.error('Error while waiting for the clear of another window:', error));
            break;
        case 'end':
            endOtherPageClear?.();
            break;
    }
});
post({ type: 'query' });

/**
 * Deletes everything the application keeps in this browser: the service workers, the databases (projects, local
 * changes not published yet), the caches, the local and session storages (the session).
 *
 * The page is blocked for `timeoutMs` at most, and the other pages of the application until the end: they close their
 * database, then reload. Once everything is cleared, this page does not open its database again: it must reload.
 */
export async function clearAllCaches({ timeoutMs = CLEAR_ALL_CACHES_TIMEOUT_MS }: { timeoutMs?: number } = {}): Promise<ClearAllCachesResult> {
    const deadline = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));
    let resumeAppDB!: () => void;
    suspendAppDB(new Promise<void>((resolve) => (resumeAppDB = resolve)));
    clearingTimeoutMs = timeoutMs;
    let overlay: HTMLElement | null = null;

    let notCleared: string[] | null = null;
    try {
        post({ type: 'start', timeoutMs });
        overlay = showBlockingOverlay(translate("Clearing the data of the application, do not close this window..."));
        window.addEventListener('beforeunload', preventUnload);
        await closeAppDB();
        // before the databases: a service worker may keep one open
        const serviceWorkers = await unregisterServiceWorkers();
        const databases = await deleteDatabases(deadline);
        // after the databases: the service workers, still running until the page reloads, may fill them again
        const caches = await deleteCaches();
        const storages = clearStorages();
        notCleared = [...serviceWorkers, ...databases, ...caches, ...storages];
    } finally {
        clearingTimeoutMs = null;
        post({ type: 'end' });
        overlay?.remove();
        window.removeEventListener('beforeunload', preventUnload);
        if (notCleared === null || notCleared.length > 0) {
            resumeAppDB();
        }
    }

    if (notCleared.length === 0) {
        console.log('✅ Deleted the service workers, databases, caches and storages of the application');
    } else {
        console.error(`❌ Not cleared after ${Math.round(timeoutMs / 1000)} s: ${notCleared.join(', ')}`);
    }
    return { notCleared };
}

async function unregisterServiceWorkers(): Promise<string[]> {
    if (!navigator.serviceWorker) {
        return [];
    }
    try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const unregistered = await Promise.all(registrations.map((registration) => registration.unregister()));
        console.log(`Unregistered ${unregistered.filter(Boolean).length} service worker(s)`);
        return registrations.filter((_, index) => !unregistered[index]).map(({ scope }) => `service worker ${scope}`);
    } catch (error) {
        console.error('Error while unregistering the service workers:', error);
        return ['service workers'];
    }
}

/** Deletes every database at the same time, each waiting until the deadline for the connections to it to close */
async function deleteDatabases(deadline: Promise<void>): Promise<string[]> {
    let names: string[] = [DB_NAME];
    try {
        names = [...new Set([DB_NAME, ...(await indexedDB.databases()).map(({ name }) => name).filter((name): name is string => !!name)])];
    } catch (error) {
        console.error('Error while listing the databases, deleting the one of the application only:', error);
    }
    const notDeleted = await Promise.all(
        names.map(async (name) => {
            const keptOpen = DATABASES_KEPT_OPEN.includes(name);
            const deletion = (
                keptOpen
                    ? emptyDatabase(name)
                    : deleteDB(name, {
                          blocked: () => console.warn(`⏳ The database ${name} is open elsewhere (another window, the service worker): waiting for it to close`),
                      })
            ).then(
                () => null,
                (error) => {
                    console.error(`❌ Error while deleting the database ${name}:`, error);
                    return `database ${name} (${error instanceof Error ? error.message : String(error)})`;
                },
            );
            const timeout = deadline.then(() => `database ${name} (still open elsewhere)`);
            const result = await Promise.race([deletion, timeout]);
            if (result === null) {
                console.log(`${keptOpen ? 'Emptied' : 'Deleted'} the database ${name}`);
            }
            return result;
        }),
    );
    return notDeleted.filter((name): name is string => name !== null);
}

/** Clears every store of a database another connection keeps open: a clear, unlike a deletion, does not wait for it */
async function emptyDatabase(name: string): Promise<void> {
    const db = await openDB(name);
    try {
        await Promise.all([...db.objectStoreNames].map((store) => db.clear(store)));
    } finally {
        db.close();
    }
}

async function deleteCaches(): Promise<string[]> {
    if (typeof caches === 'undefined') {
        return [];
    }
    try {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
        console.log(`Deleted ${keys.length} cache(s)`);
        return (await caches.keys()).map((key) => `cache ${key}`);
    } catch (error) {
        console.error('Error while deleting the caches:', error);
        return ['caches'];
    }
}

function clearStorages(): string[] {
    try {
        localStorage.clear();
        sessionStorage.clear();
        return [];
    } catch (error) {
        console.error('Error while clearing the local and session storages:', error);
        return ['local and session storages'];
    }
}

/** Another page clears: this one closes its database, is blocked, and reloads once the clear is done */
async function waitForOtherPageClear(timeoutMs: number): Promise<void> {
    if (endOtherPageClear !== null) {
        return;
    }
    const end = new Promise<void>((resolve) => {
        endOtherPageClear = resolve;
        setTimeout(resolve, timeoutMs + OTHER_PAGES_MARGIN_MS);
    });
    suspendAppDB(end);
    showBlockingOverlay(translate("The data of the application is being cleared in another window: this window reloads once it is done."));
    console.warn('The data of the application is being cleared in another window: waiting for it to reload');
    await closeAppDB();
    await end;
    Navigation.driver.reload(true);
}

/** `l()` once the localization service is there: a page receives a message while it starts too */
function translate(text: string): string {
    return typeof l === 'function' ? l(text) : text;
}

function post(message: ClearAllCachesMessage): void {
    channel?.postMessage(message);
}

function showBlockingOverlay(message: string): HTMLElement {
    const overlay = document.createElement('div');
    overlay.dataset.uiOverlay = 'clear-all-caches';
    overlay.style.cssText =
        'position: fixed; inset: 0; z-index: 2147483647; display: flex; align-items: center; justify-content: center; ' +
        'padding: 24px; background: rgba(0, 0, 0, 0.7); color: white; font-size: 1.2em; text-align: center; cursor: wait;';
    overlay.textContent = message;
    document.body.appendChild(overlay);
    return overlay;
}

function preventUnload(event: BeforeUnloadEvent): void {
    event.preventDefault();
    event.returnValue = '';
}
